import store from 'redux/store';
import { deleteZone } from 'redux/slices/lists/zonesSlice';
import { addToTransportExecutor } from 'actions/factories/addToTransportExecutor';
import { deleteExecutor } from 'actions/factories/deleteExecutor';
import type { ActionDefinitions, ActionResult } from 'actions/Action';
import type { ActionEntity } from 'actions/ActionEntity';
import { showEditZone } from './edit/showEditZone';

type ZoneEntity = Extract<ActionEntity, { type: 'zone' }>;

const isAllZone = (entities: readonly ActionEntity[]): entities is readonly ZoneEntity[] => entities.every((entity) => entity.type === 'zone');

/**
 * Action definitions for zones. `createZone` / `addToTransportZone` are scoped
 * (always present, fully-qualified); `edit` (single-only) and `delete` are
 * contextual. No `duplicate`. create / edit / delete all refresh after they
 * resolve: create and edit persist through the zone endpoints (not the slice,
 * so the list needs a refetch), and delete keeps the pagination total in sync.
 * `edit` / `deleteZone` are keyed by name.
 */
export const zoneActionDefinitions: ActionDefinitions = [
  {
    id: 'createZone',
    scope: 'type',
    label: 'New Zone',
    resolve: (ctx) => () => void showEditZone({ mode: 'create' }).then(ctx.refresh),
  },
  {
    id: 'edit',
    label: 'Edit',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (!isAllZone(entities)) return 'hidden';
      if (entities.length !== 1) return 'disabled';
      const [only] = entities;
      return () => void showEditZone({ mode: 'edit', name: only.name }).then(ctx.refresh);
    },
  },
  {
    id: 'delete',
    label: 'Delete',
    resolve: (ctx): ActionResult => {
      const { entities, refresh } = ctx;
      if (!isAllZone(entities)) return 'hidden';
      if (entities.length === 0) return 'disabled';
      return deleteExecutor(entities, (entity) => store.dispatch(deleteZone(entity.name)), {
        refresh,
        confirmBody: `Are you sure you want to delete ${entities.length === 1 ? 'this zone' : `these ${entities.length} zones`}?`,
      });
    },
  },
  {
    id: 'addToTransportZone',
    label: 'Add to Transport',
    resolve: (ctx): ActionResult => {
      const { entities } = ctx;
      if (entities.length === 0) return 'disabled';
      if (!isAllZone(entities)) return 'hidden';
      const single = entities.length === 1 ? entities[0] : null;
      return addToTransportExecutor(
        'zone',
        entities.map((entity) => entity.id),
        { objectLabel: single ? single.name : 'Selected zones' }
      );
    },
  },
];
